import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { LocalizedText } from '@/utils/localization';

/** Anything a company names once per language and lists in a modal: a pillar, an objective period. */
export interface LocalizedNameEntity {
    id: string;
    name?: LocalizedText;
}

export interface UniqueLocalizedNameParams {
    /** Everything already saved, the edited entity included. */
    existing: LocalizedNameEntity[];
    /** The entity being edited, excluded so renaming it in place is not a duplicate of itself. */
    currentId: string | undefined;
    /** The language the name is written in: the backend enforces uniqueness one language at a time. */
    language: string;
    message: TranslationKey;
}

/**
 * A zod refinement that rejects a name another entity already carries in the same language.
 * Names are compared trimmed and case-insensitively, the way a reader tells two of them apart.
 */
export const validateUniqueLocalizedName =
    ({ existing, currentId, language, message }: UniqueLocalizedNameParams) =>
    (name: string, ctx: z.RefinementCtx) => {
        const candidate = name.toLowerCase().trim();
        const isDuplicate = existing.some(
            (entity) =>
                entity.id !== currentId &&
                entity.name?.[language]?.toLowerCase().trim() === candidate,
        );

        if (isDuplicate) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    };
